import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function Carousel({ children }: { children: React.ReactNode[] }) {
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const total = children.length;

  const goTo = useCallback((i: number) => {
    setActive(Math.max(0, Math.min(i, total - 1)));
  }, [total]);

  // Swipe handling
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (touchDeltaX.current < -50) goTo(active + 1);
    else if (touchDeltaX.current > 50) goTo(active - 1);
    touchDeltaX.current = 0;
  }, [active, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goTo(active - 1);
      if (e.key === "ArrowRight") goTo(active + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, goTo]);

  return (
    <div style={{ position: "relative", overflow: "hidden", width: "100%", maxWidth: 480, margin: "0 auto", aspectRatio: "9/16" }}>
      <div
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          display: "flex",
          alignItems: "stretch",
          transition: "transform 0.3s ease",
          transform: `translateX(-${active * 100}%)`,
          height: "100%",
        }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            style={{
              minWidth: "100%",
              flex: "0 0 100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Dots + arrows */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
          padding: "16px 0",
        }}
      >
        <button
          onClick={() => goTo(active - 1)}
          disabled={active === 0}
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            backgroundColor: active === 0 ? "transparent" : "rgba(255,255,255,0.15)",
            cursor: active === 0 ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <ChevronLeft size={18} color={active === 0 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)"} />
        </button>

        <div style={{ display: "flex", gap: 8 }}>
          {Array.from({ length: total }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              style={{
                width: i === active ? 24 : 8,
                height: 8,
                borderRadius: 4,
                border: "none",
                backgroundColor: i === active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </div>

        <button
          onClick={() => goTo(active + 1)}
          disabled={active === total - 1}
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            backgroundColor: active === total - 1 ? "transparent" : "rgba(255,255,255,0.15)",
            cursor: active === total - 1 ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <ChevronRight size={18} color={active === total - 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)"} />
        </button>
      </div>
    </div>
  );
}
